import { useEffect, useRef, type RefObject } from 'react'
import type { Task, Section } from '../../../lib/db/models'

/** A render segment: optional section header followed by a group of tasks */
interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

interface NavItem {
  type: 'task' | 'section'
  id: string
}

interface UseProjectKeyboardShortcutsParams {
  savedName: string
  tasks: Task[] | undefined
  creatingPosition: 'top' | 'bottom' | 'above' | null
  creatingAfterTaskId: string | null
  segments: RenderSegment[]
  activeSegmentRef: RefObject<number>
  navClearSelection: () => void
  navSelectTask: (taskId: string) => void
  startCreating: (position: 'top' | 'bottom' | 'above') => void
  startCreatingAfter: (taskId: string) => void
  handleAddSection: (position: 'top' | 'bottom') => void
  handleDuplicate: () => void
  focusedNavIndex: number | null
  flatNavList: NavItem[]
  /** When true, skip shortcuts that target sections or create "above" tasks (e.g. Quick Notes). */
  sectionsReadonly?: boolean
  /** When false, global listener is skipped entirely. Used by TabStack to
   *  keep inactive-but-mounted ProjectPages from intercepting shortcuts. */
  isActive?: boolean
}

export function useProjectKeyboardShortcuts({
  savedName,
  tasks,
  creatingPosition,
  creatingAfterTaskId,
  segments,
  activeSegmentRef,
  navClearSelection,
  navSelectTask,
  startCreating,
  startCreatingAfter,
  handleAddSection,
  handleDuplicate,
  focusedNavIndex,
  flatNavList,
  sectionsReadonly = false,
  isActive = true,
}: UseProjectKeyboardShortcutsParams) {
  const isCreating = !!creatingPosition || !!creatingAfterTaskId

  // Refs for volatile values — read inside handler to avoid re-registration on every Dexie update
  const savedNameRef = useRef(savedName)
  savedNameRef.current = savedName
  const tasksRef = useRef(tasks)
  tasksRef.current = tasks
  const isCreatingRef = useRef(isCreating)
  isCreatingRef.current = isCreating
  const segmentsRef = useRef(segments)
  segmentsRef.current = segments
  const focusedNavIndexRef = useRef(focusedNavIndex)
  focusedNavIndexRef.current = focusedNavIndex
  const handleDuplicateRef = useRef(handleDuplicate)
  handleDuplicateRef.current = handleDuplicate
  const flatNavListRef = useRef(flatNavList)
  flatNavListRef.current = flatNavList

  // Keyboard shortcuts: "A" = add top, "B" = add bottom, Cmd/Ctrl+N = add bottom
  useEffect(() => {
    if (!isActive) return
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (!savedNameRef.current.trim()) return
      if (isCreatingRef.current) return
      if (tasksRef.current === undefined) return // still loading
      // Don't intercept keys when a dropdown/context menu is open
      if (document.querySelector('[role="menu"]')) return

      // Shift+Cmd/Ctrl+N — add new section at bottom
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'KeyN') {
        e.preventDefault()
        handleAddSection('bottom')
        return
      }

      // Cmd/Ctrl+D — duplicate selected items
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault()
        handleDuplicateRef.current()
        return
      }

      // Cmd/Ctrl+N — create task below focused task, at top of focused section, or at bottom
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault()
        const focusIdx = focusedNavIndexRef.current
        if (focusIdx !== null) {
          const item = flatNavListRef.current[focusIdx]
          if (item?.type === 'task') {
            startCreatingAfter(item.id)
            return
          }
          if (item?.type === 'section') {
            // activeSegmentRef already points to this section's segment
            navClearSelection()
            startCreating('top')
            return
          }
        }
        navClearSelection()
        startCreating('bottom')
        return
      }

      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.ctrlKey || e.metaKey || e.altKey) return

      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault()
        // Read focused item BEFORE clearing selection (clearing resets activeSegmentRef)
        const focusIdx = focusedNavIndexRef.current
        const focusedItem = focusIdx !== null ? flatNavListRef.current[focusIdx] : null
        navClearSelection()
        // Section focused → always create at top of that segment
        if (focusedItem?.type === 'section') {
          startCreating('top')
          return
        }
        const segs = segmentsRef.current
        const seg = segs[activeSegmentRef.current] ?? segs[0]
        const segHasTasks = (seg?.tasks.length ?? 0) > 0
        const pos = segHasTasks ? 'top' : 'bottom'
        startCreating(pos)
      }
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault()
        navClearSelection()
        startCreating('bottom')
      }
      if ((e.key === 'f' || e.key === 'F') && !sectionsReadonly) {
        e.preventDefault()
        navClearSelection()
        // When active segment is the unsectioned "above" area, use A-key logic
        // (top/bottom) which has a more polished CMD+Enter continue flow.
        const segs = segmentsRef.current
        const seg = segs[activeSegmentRef.current] ?? segs[0]
        if (seg && !seg.section) {
          const segHasTasks = (seg.tasks.length ?? 0) > 0
          startCreating(segHasTasks ? 'top' : 'bottom')
        } else {
          startCreating('above')
        }
      }
    }
    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isActive, handleAddSection, navClearSelection, startCreating, startCreatingAfter, activeSegmentRef])

  // IPC: create new task (triggered by File > New Task menu click)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('create-new-task', () => {
      if (!savedNameRef.current.trim() || isCreatingRef.current || tasksRef.current === undefined) return
      const focusIdx = focusedNavIndexRef.current
      if (focusIdx !== null) {
        const item = flatNavListRef.current[focusIdx]
        if (item?.type === 'task') {
          startCreatingAfter(item.id)
          return
        }
        if (item?.type === 'section') {
          navClearSelection()
          startCreating('top')
          return
        }
      }
      navClearSelection()
      startCreating('bottom')
    })
    return () => {
      unsubscribe?.()
    }
  }, [navClearSelection, startCreating, startCreatingAfter])

  // IPC: create new section (triggered by File > New Section menu click)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('create-new-section', () => {
      handleAddSection('bottom')
    })
    return () => {
      unsubscribe?.()
    }
  }, [handleAddSection])

  // IPC: duplicate items (triggered by Edit > Duplicate menu click)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('duplicate-items', () => {
      handleDuplicateRef.current()
    })
    return () => {
      unsubscribe?.()
    }
  }, [])

  // When a creating entry is open and the user clicks on an existing task,
  // capture the task ID from the DOM (via [data-task-id]) BEFORE the creating
  // entry unmounts and the layout shifts. Then select that task after all
  // handlers have settled.
  useEffect(() => {
    if (!isCreating) return

    const handlePointerDown = (e: PointerEvent) => {
      const taskEl = (e.target as HTMLElement).closest?.('[data-task-id]')
      if (!taskEl) return
      const taskId = taskEl.getAttribute('data-task-id')
      if (!taskId) return

      // Schedule after creating entry unmounts and click-outside handlers settle
      setTimeout(() => {
        navSelectTask(taskId)
      }, 50)
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [isCreating, navSelectTask])
}
